/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { Query } from 'react-apollo';

import gql from 'graphql-tag';
import URI from 'jsuri';
import { isEmpty } from 'lodash';

import API from 'js/lib/api';

import { Button, Grid, InlineNotification, Link, TextField, Typography } from '@coursera/cds-core';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';
import type {
  DownloadAcademicDisciplinesRequest,
  DownloadAcademicDisciplinesResponse,
  GenerateUploadLocationResponse,
  ValidateAcademicDisciplinesResponse,
} from '@coursera/grpc-types-academicdiscipline/coursera/proto/academicdiscipline/v1beta1/academic_discipline_api';

import S3Upload from 'bundles/enterprise-admin-program-users/utils/s3uploader';
import CSVPreUploadPreview from 'bundles/enterprise-admin-users/components/csvImportWidget/CSVPreUploadPreviewV2';
import FileUploadWidget from 'bundles/enterprise-admin-users/components/csvImportWidget/FileUploadWidgetV2';

import _t from 'i18n!nls/internal-enterprise';

const academicDisciplineAPI = API('/api/grpc/academicdiscipline/v1beta1/AcademicDisciplineAPI', { type: 'rest' });
const thirdPartyOrgAPI = API('/api/thirdPartyOrganizations.v1/', { type: 'rest' });

export const DownloadAcademicDisciplineCsvQuery = gql`
  query DownloadAcademicDisciplineCsvQuery($input: DownloadAcademicDisciplinesRequest!) {
    DownloadAcademicDisciplines(input: $input)
      @rest(
        type: "DownloadAcademicDisciplines"
        path: "grpc/academicdiscipline/v1beta1/AcademicDisciplineAPI/DownloadAcademicDisciplines"
        method: "POST"
      ) {
      presignedUrl
    }
  }
`;

const SelfServeContentTool: React.FC = () => {
  const [orgSlug, setOrgSlug] = useState<string | undefined>();
  const [orgId, setOrgId] = useState<string | undefined>();
  const [csv, setCsv] = useState<File | undefined>();
  const [S3Data, setS3Data] = useState<GenerateUploadLocationResponse | undefined>();
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [isValid, setIsValid] = useState<boolean | undefined>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const fetchOrg = () => {
    if (!orgSlug) return;

    const params = new URI().addQueryParam('q', 'bySlug').addQueryParam('slug', orgSlug).toString();

    thirdPartyOrgAPI.get(params).then((res) => {
      const id = res?.elements?.[0]?.id;
      setOrgId(id);

      Promise.resolve(academicDisciplineAPI.post('DownloadAcademicDisciplines', { data: { scopeId: id } })).then(
        (resp: DownloadAcademicDisciplinesResponse) => setDownloadUrl(resp.presignedUrl)
      );
      Promise.resolve(academicDisciplineAPI.post('GenerateUploadLocation', { data: { scopeId: id } })).then(
        (s3Location: GenerateUploadLocationResponse) => setS3Data(s3Location)
      );
    });
  };

  const getS3Location = (callback: (res: $TSFixMe) => void) => {
    return callback({ signedUploadUrl: S3Data?.presignedUrl });
  };

  const handleUploadFile = (file: File) => {
    const uploader = new S3Upload({
      getSignedUrl: getS3Location,
      onFinishS3Put: () => {
        Promise.resolve(
          academicDisciplineAPI.post('ValidateAcademicDisciplines', {
            data: { scopeId: orgId, s3Bucket: S3Data?.s3Bucket, s3Key: S3Data?.s3Key },
          })
        )
          .then((resp: ValidateAcademicDisciplinesResponse) => resp && setIsValid(true))
          .catch(() => setIsValid(false));
      },
    });

    return uploader.handleFileSelect(file);
  };

  const handleFilesDropped = ({ files }: { files: File[] }) => {
    if (!isEmpty(files)) {
      setIsValid(undefined);
      setCsv(files[0]);
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      handleUploadFile(files[0]);
    }
  };

  const handleDeletingFile = () => {
    setCsv(undefined);
  };

  const handleConfirm = () => {
    setIsUploading(true);
    Promise.resolve(
      academicDisciplineAPI.post('ImportAcademicDisciplines', {
        data: { scopeId: orgId, s3Bucket: S3Data?.s3Bucket, s3Key: S3Data?.s3Key },
      })
    )
      .then(() => {
        setIsUploading(false);
        setIsValid(undefined);
        setCsv(undefined);
      })
      .catch(() => {
        setIsUploading(false);
        setError(true);
      });
  };

  return (
    <div
      className="rc-EnterpriseToolsHome"
      css={css`
        width: 900px;

        * {
          font-size: 16px !important;
        }
      `}
    >
      <Grid container spacing={16} direction="column">
        <Grid item>
          <Typography
            variant="d2"
            css={css`
              font-size: 28px !important;
            `}
          >
            {_t('Manage the academic discipline-based curriculum for a C4C organization')}
          </Typography>
        </Grid>
        <Grid item>
          <Query<
            { DownloadAcademicDisciplines: DownloadAcademicDisciplinesResponse },
            { input: DownloadAcademicDisciplinesRequest }
          >
            query={DownloadAcademicDisciplineCsvQuery}
            variables={{ input: { scopeId: 'default' } }}
          >
            {({ data }) => {
              return data?.DownloadAcademicDisciplines?.presignedUrl ? (
                <Link variant="quiet" standalone={true} href={data.DownloadAcademicDisciplines.presignedUrl}>
                  {_t('Download the default curriculum CSV Template')}
                </Link>
              ) : null;
            }}
          </Query>
        </Grid>
        <Grid item container spacing={8}>
          <Grid item xs={10}>
            <TextField
              aria-label={_t('Organization Slug')}
              placeholder={_t('Organization Slug')}
              value={orgSlug}
              onChange={(e) => setOrgSlug(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid container item alignItems="flex-end" xs={2}>
            <Button
              variant="primary"
              size="medium"
              onClick={fetchOrg}
              css={{ fontSize: '18px', lineHeight: '2.5rem', width: '100%' }}
            >
              Find
            </Button>
          </Grid>
        </Grid>
        {orgId && S3Data ? (
          <Grid item container spacing={16} direction="column">
            <Grid item>
              <Link variant="quiet" standalone={true} href={downloadUrl}>
                {_t('Download currently active curriculum CSV')}
              </Link>
            </Grid>
            <Grid item>
              {isUploading ? (
                <div>
                  <SvgLoaderSignal size={64} />
                  <p className="m-t-1">{_t('Uploading...')}</p>
                </div>
              ) : (
                <FileUploadWidget
                  onDrop={handleFilesDropped}
                  accept={{ 'text/csv': ['.csv'] }}
                  singleFileOnly={true}
                  defaultFileMessage={_t('Upload curriculum CSV')}
                >
                  {csv && S3Data && (
                    <CSVPreUploadPreview
                      files={[csv]}
                      onDelete={handleDeletingFile}
                      status={isValid ? 'API_SUCCESS' : isValid === false ? 'API_ERROR' : 'API_IN_PROGRESS'}
                    />
                  )}
                </FileUploadWidget>
              )}
            </Grid>
            <Grid item container justifyContent="flex-end">
              <Button variant="primary" disabled={!csv || !isValid || isUploading} onClick={handleConfirm}>
                {_t('Upload Curriculum')}
              </Button>
            </Grid>
            {error && (
              <Grid item>
                <InlineNotification severity="error">{_t('Uploading curriculum failed')}</InlineNotification>
              </Grid>
            )}
          </Grid>
        ) : null}
      </Grid>
    </div>
  );
};

export default SelfServeContentTool;
